import instancedDepthMainVert from "@/components/RenderedDice/shaders/instanced_depth_main.vert";
import instancedDepthParsFrag from "@/components/RenderedDice/shaders/instanced_depth_pars.frag";
import instancedDepthParsVert from "@/components/RenderedDice/shaders/instanced_depth_pars.vert";
import { getMeshPhysicalMaterial } from "@/components/RenderedDie/components/Node/helpers/getMeshPhysicalMaterial";
import {
  createDepthMaterial,
  getCompiledDepthShader,
} from "@/components/RenderedDie/helpers/addShadow";
import {
  advanceAnimatedFrameController,
  createAnimatedFrameController,
  getAnimatedFrameData,
  recordAnimatedFrameTick,
  startAnimatedFrameController,
  writeAnimatedFrameTransform,
  type AnimatedFrameController,
} from "@/components/RenderedDie/helpers/animatedFrameData";
import { processDiceTextures } from "@/components/RenderedDie/helpers/processDiceTextures";
import { setUserDataAnimationTargets } from "@/components/RenderedDie/helpers/setUserDataAnimationTargets";
import {
  getRandomCollisionSoundPath,
  getSoundPaths,
  getSoundUrl,
  shouldSkipCollisionSound,
} from "@/components/RenderedDie/helpers/sounds";
import { updateShader } from "@/components/RenderedDie/helpers/updateShader";
import { useTextures } from "@/components/RenderedDie/hooks/useTextures";
import {
  ADDITIONAL_SCALING_FACTOR,
  ANIMATION_TARGET_ID,
  DieEventTypes,
  DieTypes,
  getMediaUrl,
  isDebug,
  PddPayloadTypes,
} from "@/constants";
import { dieConfigurations } from "@/constants/diceConfig";
import { Context } from "@/context";
import { DieEventValues } from "@/enums";
import {
  buildManifestAnimationSampler,
  type ManifestAnimationSampler,
  type SamplerOutput,
} from "@/helpers/animations/buildManifestAnimationSampler";
import { toEulerFromDegrees } from "@/helpers/toEulerFromDegrees";
import { useDicePreviewerStore } from "@/hooks/useDicePreviewerStore";
import { useRenderedDiceStore } from "@/hooks/useRenderedDiceStore";
import type { DieType, MaterialResponse } from "@/types";
import { useGLTF } from "@react-three/drei";
import { invalidate, useFrame } from "@react-three/fiber";
import { useContext, useEffect, useMemo, useRef, type FC } from "react";
import {
  InstancedBufferAttribute,
  Material,
  Matrix4,
  Mesh,
  Quaternion,
  Vector3,
  type InstancedMesh,
  type Object3D,
  type WebGLProgramParametersWithUniforms,
} from "three";

type InstancedRenderedDiceBatchProps = {
  dieType: DieType;
  ids: string[];
  setId: string;
};

type DieController = AnimatedFrameController & {
  dieValue: number;
  dissolveStartedAt?: number;
  eventTime: number;
  lastStandardEvent?: DieEventValues;
  offsetQuaternion: Quaternion;
  activeAnimations: Record<string, number>;
};

// Represents a hidden state for an instance
const hiddenMatrix = new Matrix4().makeScale(0, 0, 0);
const MIN_INSTANCE_CAPACITY = 10;

// Start with a lower per-node instance count, but scale up as dice may be spammed
const getInstanceCapacity = (count: number) => {
  let capacity = MIN_INSTANCE_CAPACITY;

  while (capacity < count) {
    capacity *= 2;
  }

  return capacity;
};

const getDisplayDieValue = (dieType: DieType, upsideValue?: number) => {
  if (dieType === DieTypes.d100) {
    return upsideValue === 0 ? 10 : (upsideValue ?? 0) / 10;
  }

  return upsideValue ?? 0;
};

// TODO: Update to support multiple materials for per mesh/material combo instancing
const getSourceMesh = (geometryScene: object) => {
  let sourceMesh: Mesh | undefined;
  (geometryScene as Object3D).updateWorldMatrix?.(true, true);

  (geometryScene as Mesh).traverse?.((child) => {
    const mesh = child as Mesh;

    if (!sourceMesh && mesh.isMesh) {
      sourceMesh = mesh;
    }
  });

  return sourceMesh;
};

const createInstancedGeometry = (
  sourceMesh: Mesh,
  count: number,
  computeTangents: boolean,
) => {
  const geometry = sourceMesh.geometry.clone();

  // Legacy Node rendering preserves GLTF node transforms. Bake them into the
  // cloned geometry so instanced meshes do not drop authoring-time scale/offset.
  // (Gift-Wrapped dice are an example of this - massive otherwise)
  geometry.applyMatrix4(sourceMesh.matrixWorld);

  if (computeTangents && !geometry.getAttribute("tangent")) {
    geometry.computeTangents();
  }

  geometry.setAttribute(
    "instanceLastEvent",
    new InstancedBufferAttribute(new Float32Array(count), 1),
  );
  geometry.setAttribute(
    "instanceLastEventTime",
    new InstancedBufferAttribute(new Float32Array(count), 1),
  );
  geometry.setAttribute(
    "instanceDieValue",
    new InstancedBufferAttribute(new Float32Array(count), 1),
  );
  geometry.setAttribute(
    "instanceTopFaceNormal",
    new InstancedBufferAttribute(new Float32Array(count * 3), 3),
  );
  geometry.setAttribute(
    "instanceDissolveValue",
    new InstancedBufferAttribute(new Float32Array(count), 1),
  );

  return geometry;
};

const createDieController = (
  id: string,
  state: ReturnType<typeof useRenderedDiceStore.getState>,
) => {
  const die = state.renderedDice.entities[id];
  const frameData = getAnimatedFrameData(die.frameData);
  const offsetRotation = toEulerFromDegrees(
    die.offset?.[0] ?? 0,
    die.offset?.[1] ?? 0,
    die.offset?.[2] ?? 0,
    "YXZ",
  );
  const offsetQuaternion = new Quaternion().setFromEuler(offsetRotation);

  return {
    ...createAnimatedFrameController(frameData),
    dieValue: getDisplayDieValue(die.dieType, die.upsideValue),
    eventTime: 0,
    lastStandardEvent: die.lastStandardEvent,
    offsetQuaternion,
    activeAnimations: {},
  } satisfies DieController;
};

export const InstancedRenderedDiceBatch: FC<
  InstancedRenderedDiceBatchProps
> = ({ dieType, ids, setId }) => {
  const manifest = useRenderedDiceStore(
    (state) => state.manifests.entities[setId],
  );
  const setCurrentEvent = useRenderedDiceStore(
    (state) => state.setCurrentEvent,
  );
  const clearRenderedRoll = useRenderedDiceStore(
    (state) => state.clearRenderedRoll,
  );
  const volume = useRenderedDiceStore((state) => state.userSettings?.volume);
  const customUrlMap = useDicePreviewerStore((state) => state.customUrlMap);

  const {
    nodeEnv: ne,
    env: e,
    renderedWorkerPort,
    user: u,
  } = useContext(Context);

  const nameJson = self.name ? JSON.parse(self.name) : {};
  const nodeEnv = ne || nameJson.nodeEnv;
  const env = e || nameJson.env;
  const user = u || nameJson.user;

  const messenger: Pick<typeof self, "postMessage"> =
    renderedWorkerPort ?? self;
  const mediaUrl = getMediaUrl(nodeEnv, env);
  const node = manifest[dieType];
  const setCodename = manifest.slug;
  const modelPath = manifest.resources.models[node.modelFile];
  const gltfUrl = customUrlMap
    ? customUrlMap[modelPath]
    : `${mediaUrl}/dice/bundles/${setCodename}/${modelPath}`;
  const materialEntry = Object.values(node.materials)[0] as MaterialResponse;
  const instanceCapacity = getInstanceCapacity(ids.length);

  // Stable reference around the ids to avoid unnecessary controller updates
  const idsKey = ids.join("|");
  const activeIds = useMemo(() => (idsKey ? idsKey.split("|") : []), [idsKey]);

  const { parser, scene } = useGLTF(gltfUrl);
  const sourceMesh = useMemo(() => {
    if (parser) setUserDataAnimationTargets(scene, parser);
    return getSourceMesh(scene);
  }, [parser, scene]);
  const textures = useTextures(setCodename, manifest.resources.textures);
  const props = useMemo(
    () => materialEntry.properties ?? {},
    [materialEntry.properties],
  );
  const geometry = useMemo(
    () =>
      sourceMesh
        ? createInstancedGeometry(
            sourceMesh,
            instanceCapacity,
            Boolean(props.glitterScale),
          )
        : undefined,
    [instanceCapacity, props.glitterScale, sourceMesh],
  );
  const { meshSizeMultiplier, values } = dieConfigurations[dieType];
  const scale =
    meshSizeMultiplier * (node.scale ?? 1.0) * ADDITIONAL_SCALING_FACTOR;
  const { dissolveTexture, numberTexture, glitterPatternTexture } =
    processDiceTextures(textures, props);

  const shaderRef = useRef<WebGLProgramParametersWithUniforms | undefined>(
    undefined,
  );
  const globalTime = useRef(0);
  const meshRef = useRef<InstancedMesh>(null);
  const controllersRef = useRef<Record<string, DieController>>({});
  const frameScratchRef = useRef({
    matrix: new Matrix4(),
    position: new Vector3(),
    quaternion: new Quaternion(),
    scaleVector: new Vector3(),
    samplerOutput: {} as SamplerOutput,
  });

  const targetName = sourceMesh?.userData?.[ANIMATION_TARGET_ID] as
    | string
    | undefined;

  // Build a sampler per manifest trigger, keyed by triggerStart (e.g.
  // "dieRemoved"). The per-frame loop walks each die's active animations,
  // looks up the sampler here, and handles the output.
  const samplersByTrigger = useMemo(() => {
    const map = new Map<string, ManifestAnimationSampler>();
    if (!targetName) {
      if (isDebug && node.animations && node.animations.length > 0) {
        console.warn(
          "Instanced batch source mesh missing animationTargetId; manifest animations will not play",
          { setId, dieType },
        );
      }
      return map;
    }
    node.animations?.forEach((entry) => {
      if (Array.isArray(entry)) return;
      const group = manifest.animationGroups?.[entry.definition];
      if (!group) return;
      const sampler = buildManifestAnimationSampler(group, targetName);
      if (sampler) map.set(entry.triggerStart, sampler);
    });
    return map;
  }, [dieType, manifest.animationGroups, node.animations, setId, targetName]);

  const soundPaths = useMemo(
    () => getSoundPaths(manifest.resources.sounds),
    [manifest.resources.sounds],
  );
  const hasManifestSounds =
    Object.keys(manifest.resources.sounds ?? {}).length > 0;

  const useDissolve =
    Boolean(props.dissolveTexture) || !samplersByTrigger.has("dieRemoved");

  const material = useMemo(() => {
    const physicalMaterial = getMeshPhysicalMaterial(
      dieType,
      sourceMesh?.material as Material,
      props,
      textures,
      undefined,
      undefined,
      undefined,
    );

    physicalMaterial.defines = {
      ...physicalMaterial.defines,
      USE_UV: "",
    };
    physicalMaterial.onBeforeCompile = (shader) => {
      updateShader(
        shader,
        props.useNumbers ?? Boolean(props.numberTexture),
        values,
        dissolveTexture,
        numberTexture,
        useDissolve,
        props.numberColor,
        props.numberHighlightColor,
        props.numberBumpScale,
        props.numbersMode,
        props.marbleAmplitude,
        props.marbleTiming,
        props.marbleZoom,
        props.glitterScale,
        glitterPatternTexture,
        props.glitterNoiseTexture
          ? textures[props.glitterNoiseTexture]
          : undefined,
        true,
      );

      shaderRef.current = shader;
    };
    physicalMaterial.customProgramCacheKey = () =>
      `${setId}-${dieType}-instanced`;

    // Force depthWrite so that instanced transparent materials don't render in the wrong order
    // when rolling. Noticeable on sets like Astral or Beyond.
    // TODO: Will have to revisit when sets with intentional depthWrite=false transparency (Planar
    // Summoning, fuzzy) become instanceable.
    physicalMaterial.depthWrite = true;

    return physicalMaterial;
  }, [
    dieType,
    dissolveTexture,
    glitterPatternTexture,
    numberTexture,
    props,
    setId,
    sourceMesh?.material,
    textures,
    useDissolve,
    values,
  ]);

  const depthMaterial = useMemo(
    () =>
      createDepthMaterial({
        customProgramCacheKey: `instanced-depth-${useDissolve ? "dissolve" : "plain"}`,
        dissolveTexture,
        materialDefines: useDissolve ? { USE_DISSOLVE: "" } : {},
        useDissolve,
        fragmentPars: instancedDepthParsFrag,
        vertexMain: instancedDepthMainVert,
        vertexPars: instancedDepthParsVert,
      }),
    [dissolveTexture, useDissolve],
  );

  useEffect(() => {
    if (!isDebug) return;

    console.log("Instanced rendered dice batch", {
      setId,
      nodeName: dieType,
      capacity: instanceCapacity,
    });
  }, [dieType, instanceCapacity, setId]);

  useEffect(
    () => () => {
      geometry?.dispose();
    },
    [geometry],
  );

  useEffect(
    () => () => {
      material.dispose();
    },
    [material],
  );

  useEffect(
    () => () => {
      depthMaterial.dispose();
    },
    [depthMaterial],
  );

  useEffect(() => {
    if (!meshRef.current) return;

    meshRef.current.customDepthMaterial = depthMaterial;
  }, [depthMaterial]);

  useEffect(() => {
    const state = useRenderedDiceStore.getState();
    const activeIdsSet = new Set(activeIds);
    const controllers = { ...controllersRef.current };

    Object.keys(controllers).forEach((id) => {
      if (!activeIdsSet.has(id)) {
        delete controllers[id];
      }
    });

    activeIds.forEach((id) => {
      if (!controllers[id] && state.renderedDice.entities[id]) {
        controllers[id] = createDieController(id, state);
      }
    });

    controllersRef.current = controllers;
  }, [activeIds]);

  useEffect(() => {
    const state = useRenderedDiceStore.getState();
    const rollIds = new Set(
      activeIds
        .map((id) => state.renderedDice.entities[id]?.rollId)
        .filter((rollId): rollId is string => Boolean(rollId)),
    );
    const emergencyDispose = Array.from(rollIds).map((rollId) =>
      setTimeout(() => {
        clearRenderedRoll(rollId);
        messenger.postMessage({
          type: PddPayloadTypes.RemoveRoll,
          payload: rollId,
        });
        invalidate();
      }, 30_000),
    );

    return () => {
      emergencyDispose.forEach((timeout) => clearTimeout(timeout));
    };
  }, [activeIds, clearRenderedRoll, messenger]);

  useFrame((_, delta) => {
    const mesh = meshRef.current;
    if (!mesh || !geometry) return;

    // Set the instance count to the current number of dice, up to the instance capacity
    mesh.count = ids.length;
    globalTime.current += delta;

    if (shaderRef.current) {
      shaderRef.current.uniforms.iGlobalTime.value = globalTime.current;
    }

    const depthShader = getCompiledDepthShader(depthMaterial);
    if (depthShader) {
      depthShader.uniforms.iGlobalTime.value = globalTime.current;
    }

    const state = useRenderedDiceStore.getState();
    const now = performance.now();
    const { matrix, position, quaternion, scaleVector, samplerOutput } =
      frameScratchRef.current;

    const lastEventAttribute = geometry.getAttribute(
      "instanceLastEvent",
    ) as InstancedBufferAttribute;
    const lastEventTimeAttribute = geometry.getAttribute(
      "instanceLastEventTime",
    ) as InstancedBufferAttribute;
    const dieValueAttribute = geometry.getAttribute(
      "instanceDieValue",
    ) as InstancedBufferAttribute;
    const topFaceNormalAttribute = geometry.getAttribute(
      "instanceTopFaceNormal",
    ) as InstancedBufferAttribute;
    const dissolveValueAttribute = geometry.getAttribute(
      "instanceDissolveValue",
    ) as InstancedBufferAttribute;
    let attributesNeedUpdate = false;

    ids.forEach((id, index) => {
      const controller = controllersRef.current[id];
      const die = state.renderedDice.entities[id];

      if (!controller || !die) {
        mesh.setMatrixAt(index, hiddenMatrix);
        return;
      }

      const currentEvent = die.lastStandardEvent;
      if (currentEvent !== controller.lastStandardEvent) {
        controller.lastStandardEvent = currentEvent;
        controller.eventTime = globalTime.current;

        if (currentEvent === DieEventValues.dieRemoved) {
          controller.dissolveStartedAt = now;
        }

        const triggerName =
          currentEvent !== undefined ? DieEventValues[currentEvent] : undefined;
        if (triggerName && samplersByTrigger.has(triggerName)) {
          controller.activeAnimations[triggerName] = now;
        }

        if (
          currentEvent === DieEventValues.rollComplete ||
          currentEvent === DieEventValues.minRoll ||
          currentEvent === DieEventValues.maxRoll
        ) {
          controller.dieValue = getDisplayDieValue(
            die.dieType,
            die.upsideValue,
          );
        }

        attributesNeedUpdate = true;
      }

      lastEventAttribute.setX(index, controller.lastStandardEvent ?? 0);
      lastEventTimeAttribute.setX(index, controller.eventTime);
      dieValueAttribute.setX(index, controller.dieValue);
      topFaceNormalAttribute.setXYZ(
        index,
        die.closestNormal?.[0] ?? 0,
        die.closestNormal?.[1] ?? 0,
        die.closestNormal?.[2] ?? 0,
      );
      dissolveValueAttribute.setX(
        index,
        controller.dissolveStartedAt === undefined
          ? 0
          : Math.max(0, (now - controller.dissolveStartedAt) / 1000),
      );
      attributesNeedUpdate = true;

      if (!controller.frameData.length) {
        mesh.setMatrixAt(index, hiddenMatrix);
        return;
      }

      if (!controller.started) {
        startAnimatedFrameController(controller, now);
        setCurrentEvent(id, {
          trigger: DieEventTypes.ROLL_BEGIN,
        });
      }

      if (!controller.completed) {
        recordAnimatedFrameTick(controller);

        const result = advanceAnimatedFrameController(controller, now);

        if (result.completed) {
          setCurrentEvent(
            id,
            {
              trigger: DieEventTypes.ROLL_COMPLETE,
            },
            undefined,
            user.id,
            (message) => messenger.postMessage(message),
          );

          setTimeout(() => {
            if (die.upsideValue === 1 || die.upsideValue === values) {
              setCurrentEvent(id, {
                trigger:
                  die.upsideValue === 1
                    ? DieEventTypes.MIN_ROLL
                    : DieEventTypes.MAX_ROLL,
              });
            }
          }, 50);
        }
      }

      writeAnimatedFrameTransform({
        controller,
        nowMs: now,
        offsetQuaternion: controller.offsetQuaternion,
        targetPosition: position,
        targetQuaternion: quaternion,
      });

      // Handle manifest animation samplers
      let sx = scale;
      let sy = scale;
      let sz = scale;
      for (const triggerName in controller.activeAnimations) {
        const sampler = samplersByTrigger.get(triggerName);
        if (!sampler) continue;

        const elapsed = (now - controller.activeAnimations[triggerName]) / 1000;

        // Reset properties before sampling again to avoid carryover from others
        samplerOutput.scale = undefined;
        samplerOutput.visibility = undefined;
        const sampled = sampler.sample(elapsed, samplerOutput);

        if (sampled.scale) {
          sx *= sampled.scale.x;
          sy *= sampled.scale.y;
          sz *= sampled.scale.z;
        }

        if (sampled.visibility === 0) {
          sx = 0;
          sy = 0;
          sz = 0;
          break;
        }
      }
      scaleVector.set(sx, sy, sz);

      matrix.compose(position, quaternion, scaleVector);
      mesh.setMatrixAt(index, matrix);

      if (volume && !shouldSkipCollisionSound(currentEvent)) {
        const frameVolume =
          die.frameData?.[controller.currentFrameTick]?.collisionVolume;
        const soundPath = getRandomCollisionSoundPath(soundPaths);

        if (frameVolume && soundPath) {
          messenger.postMessage({
            type: PddPayloadTypes.PlaySound,
            payload: {
              url: getSoundUrl({
                customUrlMap,
                hasManifestSounds,
                mediaUrl,
                setCodename: manifest.slug,
                soundPath,
              }),
              volume: frameVolume,
              isCollision: true,
            },
          });
        }
      }

      if (controller.dissolveStartedAt !== undefined)
        attributesNeedUpdate = true;
    });

    mesh.instanceMatrix.needsUpdate = true;

    if (attributesNeedUpdate) {
      lastEventAttribute.needsUpdate = true;
      lastEventTimeAttribute.needsUpdate = true;
      dieValueAttribute.needsUpdate = true;
      topFaceNormalAttribute.needsUpdate = true;
      dissolveValueAttribute.needsUpdate = true;
    }

    invalidate();
  });

  if (!geometry) return null;

  return (
    <instancedMesh
      ref={meshRef}
      args={[geometry, material, instanceCapacity]}
      castShadow
      customDepthMaterial={depthMaterial}
    />
  );
};
